import { snapshot as authSnapshot } from "@internal/auth";
import { getDb, type PlannerDoc } from "@/lib/db";
import {
  DIARY_MEALS_TYPE,
  type DiaryMeal,
  type DiaryMealsData,
  diaryMealsId,
} from "@/lib/diary";

function currentUserId(): string {
  const user = authSnapshot().user;
  if (!user) throw new Error("Not signed in");
  return user.id;
}

const writeQueues = new Map<string, Promise<unknown>>();
function enqueue<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const prev = writeQueues.get(key) ?? Promise.resolve();
  const next = prev.then(fn, fn);
  writeQueues.set(
    key,
    next.catch(() => {}),
  );
  return next;
}

/**
 * Rewrite the meals stored for `date`. Rows are never deleted — a date whose
 * last meal goes stays as a row with no meals, as body measurements do.
 * Resolves to the meals now stored.
 */
async function updateMeals(
  date: string,
  change: (meals: DiaryMeal[]) => DiaryMeal[],
): Promise<DiaryMeal[]> {
  const userId = currentUserId();
  const db = await getDb();
  const id = diaryMealsId(userId, date);
  return enqueue(id, async () => {
    const existing = await db.daily_planner.findOne(id).exec();
    const current = existing
      ? ((existing.toJSON() as PlannerDoc).data as unknown as DiaryMealsData)
          .meals
      : [];
    const next: DiaryMealsData = { date, meals: change(current ?? []) };
    if (existing) {
      await existing.patch({
        data: next as unknown as Record<string, unknown>,
      });
    } else {
      await db.daily_planner.insert({
        id,
        type: DIARY_MEALS_TYPE,
        user_id: userId,
        data: next as unknown as Record<string, unknown>,
      });
    }
    return next.meals;
  });
}

/** Add `meal`, or replace the stored meal with its id. */
export function saveDiaryMeal(
  date: string,
  meal: DiaryMeal,
): Promise<DiaryMeal[]> {
  return updateMeals(date, (meals) =>
    meals.some((m) => m.id === meal.id)
      ? meals.map((m) => (m.id === meal.id ? meal : m))
      : [...meals, meal],
  );
}

export function deleteDiaryMeal(
  date: string,
  mealId: string,
): Promise<DiaryMeal[]> {
  return updateMeals(date, (meals) => meals.filter((m) => m.id !== mealId));
}
